import type { FilterOption, SelectedFilterValue } from '@/components/shared';

export interface GeoHierarchy {
  regionOptions: FilterOption[];
  countryOptions: FilterOption[];
}

interface GeoLookups {
  regionByValue: Map<string, FilterOption>;
  countryByValue: Map<string, FilterOption>;
  countriesByRegionId: Map<string, FilterOption[]>;
}

interface GeoSyncResult {
  geoRegion: SelectedFilterValue[];
  country: SelectedFilterValue[];
}

export function buildGeoLookups(hierarchy: GeoHierarchy): GeoLookups {
  const regionByValue = new Map(hierarchy.regionOptions.map((o) => [o.value, o]));
  const countryByValue = new Map(hierarchy.countryOptions.map((o) => [o.value, o]));
  // Each country can belong to multiple regions (M:N — Mexico ∈ {NA, LATAM});
  // index it under each of its region IDs so region-driven lookups find it.
  const countriesByRegionId = new Map<string, FilterOption[]>();
  for (const c of hierarchy.countryOptions) {
    for (const regionId of c.regionIds ?? []) {
      const list = countriesByRegionId.get(regionId);
      if (list) list.push(c);
      else countriesByRegionId.set(regionId, [c]);
    }
  }
  return { regionByValue, countryByValue, countriesByRegionId };
}

export function syncRegionChange(
  newRegions: SelectedFilterValue[],
  prevRegions: SelectedFilterValue[],
  currentCountries: SelectedFilterValue[],
  lookups: GeoLookups,
): GeoSyncResult {
  const { regionByValue, countryByValue, countriesByRegionId } = lookups;

  const prevRegionValues = new Set(prevRegions.map((r) => r.value));
  const newRegionValues = new Set(newRegions.map((r) => r.value));

  const addedRegionIds = new Set(
    newRegions
      .filter((r) => !prevRegionValues.has(r.value))
      .map((r) => regionByValue.get(r.value)?.id)
      .filter(Boolean) as string[],
  );

  const removedRegionIds = new Set(
    prevRegions
      .filter((r) => !newRegionValues.has(r.value))
      .map((r) => regionByValue.get(r.value)?.id)
      .filter(Boolean) as string[],
  );

  // The set of region IDs that remain selected after the change — used to
  // decide whether a country dropped from one region is still anchored to
  // another (Mexico stays under LATAM if user removed NA but kept LATAM).
  const remainingRegionIds = new Set(
    newRegions.map((r) => regionByValue.get(r.value)?.id).filter(Boolean) as string[],
  );

  const existingValues = new Set(currentCountries.map((c) => c.value));
  let countries = [...currentCountries];

  for (const regionId of addedRegionIds) {
    for (const country of countriesByRegionId.get(regionId) ?? []) {
      if (!existingValues.has(country.value)) {
        countries.push({
          id: country.id,
          label: country.label,
          value: country.value,
          type: 'standard' as const,
        });
        existingValues.add(country.value);
      }
    }
  }

  if (removedRegionIds.size > 0) {
    countries = countries.filter((c) => {
      const regionIds = countryByValue.get(c.value)?.regionIds ?? [];
      // Drop the country only if every region it belongs to is either
      // (a) being removed, or (b) was never selected. If any of its regions
      // is still selected, keep it.
      const stillAnchored = regionIds.some((id) => remainingRegionIds.has(id));
      return regionIds.length === 0 || stillAnchored;
    });
  }

  return { geoRegion: newRegions, country: countries };
}

export function syncCountryChange(
  newCountries: SelectedFilterValue[],
  hierarchy: GeoHierarchy,
  lookups: GeoLookups,
): GeoSyncResult {
  const { countriesByRegionId } = lookups;
  const selectedValues = new Set(newCountries.map((c) => c.value));

  // A region is "selected" when every one of its countries is selected. With
  // M:N membership, a country can satisfy multiple regions simultaneously.
  const newRegions: SelectedFilterValue[] = hierarchy.regionOptions
    .filter((region) => {
      const regionCountries = countriesByRegionId.get(region.id) ?? [];
      return (
        regionCountries.length > 0 && regionCountries.every((c) => selectedValues.has(c.value))
      );
    })
    .map((r) => ({
      id: r.id,
      label: r.label,
      value: r.value,
      type: 'standard' as const,
    }));

  return { geoRegion: newRegions, country: newCountries };
}
